import { createStyles, makeStyles, Tooltip } from "@material-ui/core";
import * as React from "react";
import {
    Step1Availability,
    Step2FormattedResumeMobile,
    Step3AdditionalInformation,
} from ".";
import {
    Button,
    DialogActions,
    DialogContent,
    ExclamationIcon,
    FullScreenDialog,
    RelodeTheme,
    Text,
} from "../../../";
import { CandidateReviewStepsEnum } from "../types";

import { Step4CandidateVerification } from "./step4CandidateVerification";
import { useCandidateReviewForm } from "./useCandidateReviewForm";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            candidateName: {
                marginBottom: theme.spacing(2),
            },
            wizardHeaderText: {
                marginRight: theme.spacing(1),
            },
        }),
    { name: "CandidateReviewMobile" }
);

interface CandidateReviewMobileProps {
    onClose: () => void;
    handlePrevious: () => void;
    handleNext: () => Promise<void>;
    handleFieldChange: (
        field: string
    ) => (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => void;
    handleFormattedResumeUpload: (
        event: React.ChangeEvent<HTMLInputElement>
    ) => Promise<boolean>;
    candidateName: string;
    jobName: string;
    showVerification: boolean;
    activeStepId: CandidateReviewStepsEnum;
}

export const CandidateReviewMobile = (props: CandidateReviewMobileProps) => {
    const classes = useStyles({});
    const {
        onClose,
        handleNext,
        handlePrevious,
        handleFieldChange,
        handleFormattedResumeUpload,
        candidateName,
        jobName,
        showVerification,
        activeStepId,
    } = props;

    const { errors, dirty } = useCandidateReviewForm();
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    const handleOpenTooltip = () => {
        setTooltipOpen(true);
    };

    const handleCloseTooltip = () => {
        setTooltipOpen(false);
    };

    const renderSteps = () => {
        switch (activeStepId) {
            case CandidateReviewStepsEnum.Interview:
                return <Step1Availability onFieldChange={handleFieldChange} />;
            case CandidateReviewStepsEnum.FormattedResume:
                return (
                    <Step2FormattedResumeMobile
                        onFormattedResumeUpload={handleFormattedResumeUpload}
                    />
                );
            case CandidateReviewStepsEnum.AdditionalInformation:
                return (
                    <Step3AdditionalInformation
                        onFieldChange={handleFieldChange}
                    />
                );
            case CandidateReviewStepsEnum.CandidateVerification:
                return <Step4CandidateVerification />;
        }
    };

    return (
        <FullScreenDialog
            open={true}
            title={
                !!errors.stepTwo ? (
                    <>
                        <Text
                            variant="h1"
                            color="primary"
                            className={classes.wizardHeaderText}
                        >
                            review details
                        </Text>

                        <Tooltip
                            title="Please resolve any errors below before continuing."
                            open={tooltipOpen}
                            onClose={handleCloseTooltip}
                            leaveTouchDelay={3000}
                        >
                            <span>
                                <ExclamationIcon
                                    color="error"
                                    onClick={handleOpenTooltip}
                                />
                            </span>
                        </Tooltip>
                    </>
                ) : (
                    <Text variant="h1" color="primary">
                        review details
                    </Text>
                )
            }
            onClose={onClose}
        >
            <DialogContent>
                <Text
                    component="div"
                    variant="h2"
                    color="inherit"
                    className={classes.candidateName}
                >
                    {candidateName}
                </Text>
                <Text component="div">{jobName}</Text>
                {renderSteps()}
            </DialogContent>
            <DialogActions>
                <Button
                    id="next"
                    fullWidth
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={handleNext}
                >
                    {(activeStepId ===
                        CandidateReviewStepsEnum.AdditionalInformation &&
                        !showVerification) ||
                    activeStepId ===
                        CandidateReviewStepsEnum.CandidateVerification
                        ? "finish"
                        : "next"}
                </Button>
                {activeStepId !== CandidateReviewStepsEnum.Interview ? (
                    <Button
                        id="next"
                        fullWidth
                        size="small"
                        color="default"
                        variant="contained"
                        onClick={handlePrevious}
                    >
                        back
                    </Button>
                ) : null}
            </DialogActions>
        </FullScreenDialog>
    );
};
